import { useTranslation } from 'react-i18next';

import externalLinkIcon from './outside-link-icon.svg';
import { getColorVars } from '../../utils/functions';

interface Props {
  children: JSX.Element | string | null;
  className?: string;
  onClick?: () => void;
  href?: string;
  external?: boolean;
  variant?: 'textonly' | 'transparent';
  icon?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

const Button: React.FC<Props> = (props: Props): JSX.Element => {
  const { t } = useTranslation(['common']);
  const { bgColor, borderColor, textColor } = getColorVars();

  let variantClassNames = '';

  switch (props.variant) {
    case 'transparent':
      variantClassNames = `bg-white border ${
        props.className?.includes('border-') ? '' : borderColor
      } ${textColor}`;
      break;

    case 'textonly':
      variantClassNames = 'bg-transparent';
      break;

    default:
      variantClassNames = `${bgColor} text-white`;
  }

  const classNames = [
    variantClassNames,
    'uppercase',
    'font-rukaNeue',
    'tracking-widest',
    'text-base',
    'p-3',
    'inline-block',
    'rounded',
    'text-center',
    'justify-center',
    'no-underline',
    'font-normal',
    props.className,
  ].join(' ');

  let icon = null;
  if (props.icon) {
    icon = props.icon;
  } else if (props.external) {
    icon = externalLinkIcon;
  }

  let component;

  if (!props.href) {
    component = (
      <button
        disabled={props.disabled}
        aria-disabled={props.disabled}
        onClick={props.onClick}
        type={props.type || 'button'}
        className={classNames}
      >
        {props.children}

        {icon && (
          <img
            className='inline-block ml-2 h-5'
            src={icon}
            alt={t('External link') ?? ''}
          />
        )}
      </button>
    );
  } else {
    component = (
      <a
        href={props.href}
        className={`${classNames} flex items-center`}
        target={props.external ? '_blank' : ''}
        rel={props.external ? 'noreferrer' : ''}
        onClick={props.onClick ?? undefined}
      >
        <span>{props.children}</span>

        {icon && (
          <img
            className='inline-block ml-2 h-4'
            src={icon}
            alt={t('External link') ?? ''}
          />
        )}
      </a>
    );
  }

  return <>{component}</>;
};

export default Button;
